import { useEffect } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import '@maplibre/maplibre-gl-leaflet';

// OpenFreeMap serves the open-source Positron style (the same cartography as
// the retired CARTO light_all rasters) keyless and with
// Access-Control-Allow-Origin: * — which the PNG export relies on.
const POSITRON_STYLE_URL = 'https://tiles.openfreemap.org/styles/positron';

// OpenFreeMap itself doesn't require attribution; OpenMapTiles (style/schema)
// and OpenStreetMap (data) do.
export const BASEMAP_ATTRIBUTION =
  '<a href="https://openfreemap.org">OpenFreeMap</a> &copy; <a href="https://www.openmaptiles.org/">OpenMapTiles</a> Data: &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>';

// Probe before adding the layer: if maplibregl.Map's constructor throws
// mid-onAdd, Leaflet has already registered the layer's map-event handlers
// and every later pan/zoom would crash on the missing GL map.
function webglSupported(): boolean {
  try {
    const canvas = document.createElement('canvas');
    return !!(canvas.getContext('webgl2') || canvas.getContext('webgl'));
  } catch {
    return false;
  }
}

/**
 * Vector basemap rendered by MapLibre GL into the Leaflet tile pane (z 200),
 * so the wahlkreise (350) and overlay (400) panes keep stacking above it.
 * If WebGL is unavailable the widget degrades to choropleth/bubbles on the
 * plain container background instead of crashing.
 */
export function GlBasemapLayer() {
  const map = useMap();

  useEffect(() => {
    if (!webglSupported()) {
      console.warn('[investments-widget] WebGL unavailable — rendering without basemap');
      return;
    }
    const layer = L.maplibreGL({
      style: POSITRON_STYLE_URL,
      // The PNG export drawImage()s the GL canvas outside MapLibre's own
      // render frame; without preserveDrawingBuffer it reads back blank.
      canvasContextAttributes: { preserveDrawingBuffer: true },
      // The plugin forwards options to maplibregl.Map, which would mount
      // its own attribution control inside the tile pane — suppress it and
      // credit via Leaflet's control below instead.
      attributionControl: false,
    });
    layer.addTo(map);
    layer.getMaplibreMap().on('error', (event) => {
      console.warn('[investments-widget] basemap error:', event.error ?? event);
    });
    map.attributionControl?.addAttribution(BASEMAP_ATTRIBUTION);
    return () => {
      map.attributionControl?.removeAttribution(BASEMAP_ATTRIBUTION);
      map.removeLayer(layer);
    };
  }, [map]);

  return null;
}
